import React, { useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import axios from "axios";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  TableCell,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { getToken, base_url } from "../../config/config";

const ManagementCheckInTable = () => {
  const [data, setData] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const savedName = localStorage.getItem("employeeName");
  const [editedValues, setEditedValues] = useState({
    user: savedName,
    checkInTime: "",
    checkOutTime: "",
    checkInDate: "",
  });
  const [newCheckIn, setNewCheckIn] = useState({
    user: savedName,
    checkInTime: "",
    checkOutTime: "",
    checkInDate: "",
  });
  const navigate = useNavigate();
  const token = getToken();

  // Fetch data from the server
  useEffect(() => {
    axios
      .get(`${base_url}/v1/getAllCheckIns`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const processedData = response.data.map((row) => ({
          ...row,
          totalTime: calculateTotalTime(row.checkInTime, row.checkOutTime),
          overTime: calculateOverTime(
            row.checkInTime,
            row.checkOutTime,
            row.checkInDate,
          ), // pass checkInDate here
        }));
        setData(processedData);
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
      });
  }, []);

  // Function to calculate total time
  const calculateTotalTime = (checkInTime, checkOutTime) => {
    if (!checkInTime || !checkOutTime) return "N/A";

    const [checkInHours, checkInMinutes] = checkInTime.split(":").map(Number);
    const [checkOutHours, checkOutMinutes] = checkOutTime
      .split(":")
      .map(Number);

    const start = new Date(0, 0, 0, checkInHours, checkInMinutes, 0);
    const end = new Date(0, 0, 0, checkOutHours, checkOutMinutes, 0);

    // Calculate difference in hours
    let diff = (end - start) / (1000 * 60 * 60);

    // Subtract 45 minutes as break time (convert 45 minutes to hours)
    diff -= 45 / 60;

    const hours = Math.floor(diff);
    const minutes = Math.round((diff - hours) * 60);

    return `${hours}h ${minutes}m`;
  };

  // Function to calculate overtime if total time > 8 hours
  // Function to calculate overtime based on the day of the week
  const calculateOverTime = (checkInTime, checkOutTime, checkInDate) => {
    // Convert the checkInDate to a Date object and determine the day of the week
    const dayOfWeek = new Date(checkInDate).getDay(); // Sunday is 0, Monday is 1, ..., Friday is 5

    // Define official working hours based on the day of the week
    const officialHours = dayOfWeek === 5 ? 7 : 8.25; // 7 hours for Friday, 8h 15m (8.25) for other days

    // Calculate total hours
    const totalHours = parseFloat(
      calculateTotalTime(checkInTime, checkOutTime).split("h")[0],
    );

    // Calculate overtime
    if (totalHours > officialHours) {
      return `${(totalHours - officialHours).toFixed(2)}h`;
    }

    return `0h`;
  };

  // Define columns for MaterialReactTable
  const columns = [
    { header: "User", accessorKey: "user" },
    { header: "Check In Time", accessorKey: "checkInTime" },
    { header: "Check Out Time", accessorKey: "checkOutTime" },
    {
      header: "Check In Date",
      accessorKey: "checkInDate",
      Cell: ({ cell }) => (
        <Button
          onClick={() =>
            handleViewTaskLogs(cell.getValue(), cell.row.original.user)
          }
          variant='outlined'>
          {cell.getValue()}
        </Button>
      ),
    },
    { header: "Total Time", accessorKey: "totalTime" },
    {
      header: "Overtime",
      accessorKey: "overTime",
      Cell: ({ cell }) => {
        const overtime = cell.getValue();
        const isPositive = parseFloat(overtime) > 0;
        const style = {
          color: isPositive ? "green" : "red",
        };
        return <TableCell style={style}>{overtime}</TableCell>;
      },
    },
    {
      header: "Actions",
      id: "actions",
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <IconButton color='primary' onClick={() => handleEditRow(row)}>
            <EditIcon />
          </IconButton>
          <IconButton color='secondary' onClick={() => handleDeleteRow(row)}>
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  // Handle opening the edit modal
  const handleEditRow = (row) => {
    setCurrentRow(row);
    setEditedValues({
      user: row.original.user,
      checkInTime: row.original.checkInTime,
      checkOutTime: row.original.checkOutTime,
      checkInDate: row.original.checkInDate,
    });
    setOpenEditModal(true);
  };

  // Handle opening the add modal
  const handleAddOpen = () => {
    setNewCheckIn({
      user: savedName,
      checkInTime: "",
      checkOutTime: "",
      checkInDate: "",
    });
    setOpenAddModal(true);
  };

  // Handle changes in edit modal input fields
  const handleEditChange = (event) => {
    setEditedValues({
      ...editedValues,
      [event.target.name]: event.target.value,
    });
  };

  // Handle changes in add modal input fields
  const handleAddChange = (event) => {
    setNewCheckIn({
      ...newCheckIn,
      [event.target.name]: event.target.value,
    });
  };

  // Handle saving the edited row
  const handleSaveEdit = () => {
    axios
      .put(
        // need to update
        `${base_url}/v1/updateCheckIn/${currentRow.original._id}`,
        editedValues,
      )
      .then((response) => {
        const updatedData = [...data];
        const updatedRow = {
          ...response.data,
          totalTime: calculateTotalTime(
            response.data.checkInTime,
            response.data.checkOutTime,
          ),
          overTime: calculateOverTime(
            response.data.checkInTime,
            response.data.checkOutTime,
          ),
        };
        updatedData[currentRow.index] = updatedRow;
        setData(updatedData);
        setOpenEditModal(false);
      })
      .catch((error) => {
        console.error("There was an error updating the data!", error);
      });
  };

  // Handle saving a new check-in record
  const handleSaveNew = () => {
    axios
      .post(`${base_url}/v1/createCheckIn`, newCheckIn) // need to update
      .then((response) => {
        const newRecord = {
          ...response.data,
          totalTime: calculateTotalTime(
            response.data.checkInTime,
            response.data.checkOutTime,
          ),
          overTime: calculateOverTime(
            response.data.checkInTime,
            response.data.checkOutTime,
          ),
        };
        setData((prevData) => [...prevData, newRecord]);
        setOpenAddModal(false);
      })
      .catch((error) => {
        console.error("There was an error creating the check-in!", error);
      });
  };

  // Handle row deletions
  const handleDeleteRow = (row) => {
    axios
      .delete(`${base_url}/v1/deleteCheckIn/${row.original._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setData((prevData) =>
          prevData.filter((_, index) => index !== row.index),
        );
      })
      .catch((error) => {
        console.error("There was an error deleting the data!", error);
      });
  };

  // Handle viewing task logs
  const handleViewTaskLogs = (date, employeeName) => {
    navigate(
      `/task-logs?date=${encodeURIComponent(
        date,
      )}&employeeName=${encodeURIComponent(employeeName)}`,
    );
  };

  return (
    <>
      <Button
        variant='contained'
        color='primary'
        startIcon={<AddIcon />}
        onClick={handleAddOpen}
        style={{ marginBottom: "16px" }}>
        Add Check-In
      </Button>

      <MaterialReactTable
        columns={columns}
        data={data}
        editingMode='modal' // Enables modal editing if needed
        enableRowEditing // Required for editing
        enableRowDeleting // Required for deleting
      />

      {/* Edit Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>Edit Check-In</DialogTitle>
        <DialogContent>
          <TextField
            margin='dense'
            label='User'
            type='text'
            fullWidth
            disabled
            name='user'
            value={savedName}
            onChange={handleEditChange}
          />
          <TextField
            margin='dense'
            label='Check In Time'
            type='time'
            fullWidth
            name='checkInTime'
            value={editedValues.checkInTime}
            onChange={handleEditChange}
          />
          <TextField
            margin='dense'
            label='Check Out Time'
            type='time'
            fullWidth
            name='checkOutTime'
            value={editedValues.checkOutTime}
            onChange={handleEditChange}
          />
          <TextField
            margin='dense'
            label='Check In Date'
            type='date'
            fullWidth
            name='checkInDate'
            value={editedValues.checkInDate}
            onChange={handleEditChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add New Check-In</DialogTitle>
        <DialogContent>
          <TextField
            margin='dense'
            label='User'
            type='text'
            fullWidth
            disabled
            name='user'
            value={savedName}
            onChange={handleAddChange}
          />
          <TextField
            margin='dense'
            label='Check In Time'
            type='time'
            fullWidth
            name='checkInTime'
            value={newCheckIn.checkInTime}
            onChange={handleAddChange}
          />
          <TextField
            margin='dense'
            label='Check Out Time'
            type='time'
            fullWidth
            name='checkOutTime'
            value={newCheckIn.checkOutTime}
            onChange={handleAddChange}
          />
          <TextField
            margin='dense'
            label='Check In Date'
            type='date'
            fullWidth
            name='checkInDate'
            value={newCheckIn.checkInDate}
            onChange={handleAddChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleSaveNew} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagementCheckInTable;
